import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Stack,
    Autocomplete,
    TextField
} from '@mui/material';
import { DataGrid, GridColDef, GridRowsProp, GridRowModel } from '@mui/x-data-grid';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import apiService from '../services/api.service';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../store/slices/uiSlice';

interface CostCenterMapping {
    mappingid?: number;
    departmentcode: string;
    departmentname: string;
    divisioncode?: string;
    divisionname?: string;
    locationcode?: string;
    locationname?: string;
    projectcode?: string;
    projectname?: string;
}

interface DistributionRule {
    keycode: string;
    data: {
        FactorCode: string;
        FactorDescription: string;
        InWhichDimension: number;
    };
}

const CostCenterMapping: React.FC = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [mappings, setMappings] = useState<GridRowsProp>([]);
    const [divisions, setDivisions] = useState<DistributionRule[]>([]);
    const [locations, setLocations] = useState<DistributionRule[]>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [error, setError] = useState<string>('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;

            // Load distribution rules for all dimensions
            const [deptRes, divRes, locRes, projRes, mappingRes] = await Promise.all([
                apiService.getDistributionRules(selectedCompanyId, 2), // Department
                apiService.getDistributionRules(selectedCompanyId, 1), // Division
                apiService.getDistributionRules(selectedCompanyId, 4), // Location
                apiService.getProjects(), // Projects
                apiService.getCostCenterMappings(selectedCompanyId)
            ]);

            const deptData = deptRes?.data || [];
            const divData = divRes?.data || [];
            const locData = locRes?.data || [];
            const projData = (projRes?.data || []).map((p: any) => ({
                code: p.Code,
                name: p.Name
            }));

            setDivisions(divData);
            setLocations(locData);
            setProjects(projData);

            // Create mapping rows for all departments
            const existingMappings = mappingRes?.data || [];
            const mappingMap = new Map<string, CostCenterMapping>(
                existingMappings.map((m: CostCenterMapping) => [m.departmentcode, m])
            );

            const rows = deptData.map((dept: DistributionRule, index: number) => {
                const existing = mappingMap.get(dept.data.FactorCode);
                return {
                    id: existing?.mappingid || `new-${index}`,
                    mappingid: existing?.mappingid,
                    departmentcode: dept.data.FactorCode,
                    departmentname: dept.data.FactorDescription,
                    divisioncode: existing?.divisioncode || '',
                    divisionname: existing?.divisionname || '',
                    locationcode: existing?.locationcode || '',
                    locationname: existing?.locationname || '',
                    projectcode: existing?.projectcode || '',
                    projectname: existing?.projectname || ''
                };
            });

            setMappings(rows);
        } catch (err: any) {
            setError(err.message || 'Failed to load data');
            dispatch(showSnackbar({
                message: err.message || 'Failed to load data',
                severity: 'error'
            }));
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;

            // Prepare mappings data
            const mappingsToSave = mappings.map((row: any) => ({
                departmentCode: row.departmentcode,
                departmentName: row.departmentname,
                divisionCode: row.divisioncode || null,
                divisionName: row.divisionname || null,
                locationCode: row.locationcode || null,
                locationName: row.locationname || null,
                projectCode: row.projectcode || null,
                projectName: row.projectname || null
            }));

            await apiService.batchUpdateCostCenterMappings(mappingsToSave, selectedCompanyId);

            dispatch(showSnackbar({
                message: 'Cost center mappings saved successfully',
                severity: 'success'
            }));

            // Reload data to get updated IDs
            await loadData();
        } catch (err: any) {
            dispatch(showSnackbar({
                message: err.message || 'Failed to save mappings',
                severity: 'error'
            }));
        } finally {
            setSaving(false);
        }
    };

    const processRowUpdate = (newRow: GridRowModel) => {
        setMappings((prev) =>
            prev.map((row) => (row.id === newRow.id ? newRow : row))
        );
        return newRow;
    };

    const columns: GridColDef[] = [
        {
            field: 'departmentcode',
            headerName: 'Department Code',
            width: 150,
            editable: false
        },
        {
            field: 'departmentname',
            headerName: 'Department Name',
            width: 250,
            editable: false
        },
        {
            field: 'divisioncode',
            headerName: 'Division',
            width: 250,
            editable: true,
            renderEditCell: (params) => (
                <Autocomplete
                    value={divisions.find(d => d.data.FactorCode === params.value) || null}
                    onChange={(_, newValue) => {
                        params.api.setEditCellValue({
                            id: params.id,
                            field: 'divisioncode',
                            value: newValue?.data.FactorCode || ''
                        });
                        params.api.setEditCellValue({
                            id: params.id,
                            field: 'divisionname',
                            value: newValue?.data.FactorDescription || ''
                        });
                    }}
                    options={divisions}
                    getOptionLabel={(option) => `${option.data.FactorCode} - ${option.data.FactorDescription}`}
                    renderInput={(params) => <TextField {...params} size="small" />}
                    fullWidth
                    size="small"
                />
            ),
            valueGetter: (params) => {
                const div = divisions.find(d => d.data.FactorCode === params.row.divisioncode);
                return div ? `${div.data.FactorCode} - ${div.data.FactorDescription}` : params.row.divisioncode || '';
            }
        },
        {
            field: 'locationcode',
            headerName: 'Location',
            width: 250,
            editable: true,
            renderEditCell: (params) => (
                <Autocomplete
                    value={locations.find(l => l.data.FactorCode === params.value) || null}
                    onChange={(_, newValue) => {
                        params.api.setEditCellValue({
                            id: params.id,
                            field: 'locationcode',
                            value: newValue?.data.FactorCode || ''
                        });
                        params.api.setEditCellValue({
                            id: params.id,
                            field: 'locationname',
                            value: newValue?.data.FactorDescription || ''
                        });
                    }}
                    options={locations}
                    getOptionLabel={(option) => `${option.data.FactorCode} - ${option.data.FactorDescription}`}
                    renderInput={(params) => <TextField {...params} size="small" />}
                    fullWidth
                    size="small"
                />
            ),
            valueGetter: (params) => {
                const loc = locations.find(l => l.data.FactorCode === params.row.locationcode);
                return loc ? `${loc.data.FactorCode} - ${loc.data.FactorDescription}` : params.row.locationcode || '';
            }
        },
        {
            field: 'projectcode',
            headerName: 'Project',
            width: 250,
            editable: true,
            renderEditCell: (params) => (
                <Autocomplete
                    value={projects.find(p => p.code === params.value) || null}
                    onChange={(_, newValue) => {
                        params.api.setEditCellValue({
                            id: params.id,
                            field: 'projectcode',
                            value: newValue?.code || ''
                        });
                        params.api.setEditCellValue({
                            id: params.id,
                            field: 'projectname',
                            value: newValue?.name || ''
                        });
                    }}
                    options={projects}
                    getOptionLabel={(option) => `${option.code} - ${option.name}`}
                    renderInput={(params) => <TextField {...params} size="small" />}
                    fullWidth
                    size="small"
                />
            ),
            valueGetter: (params) => {
                const proj = projects.find(p => p.code === params.row.projectcode);
                return proj ? `${proj.code} - ${proj.name}` : params.row.projectcode || '';
            }
        }
    ];

    return (
        <Box component="section" aria-labelledby="ccm-heading" sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={{ xs: 1.5, sm: 0 }}
                    mb={3}
                >
                    <Typography
                        id="ccm-heading"
                        variant="h5"
                        component="h1"
                        sx={{ fontSize: { xs: 18, sm: 22, md: 24 }, fontWeight: 700 }}
                    >
                        Cost Center Mapping
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={loadData}
                            disabled={loading || saving}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={loading || saving}
                        >
                            {saving ? 'Saving...' : 'Save All'}
                        </Button>
                    </Stack>
                </Stack>

                <Typography variant="body2" color="text.secondary" mb={2}>
                    Map each Department (Dimension 2) to its corresponding Division (Dimension 1) and Location (Dimension 4).
                    Click on a cell to edit. Changes are saved when you click "Save All".
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <DataGrid
                        rows={mappings}
                        columns={columns}
                        autoHeight
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 20, page: 0 }
                            }
                        }}
                        pageSizeOptions={[10, 20, 50, 100]}
                        disableRowSelectionOnClick
                        processRowUpdate={processRowUpdate}
                        sx={{
                            '& .MuiDataGrid-cell': {
                                cursor: 'pointer'
                            }
                        }}
                    />
                )}
            </Paper>
        </Box>
    );
};

export default CostCenterMapping;
